/**
 * What a person means by "Export", asked once, in a dialog.
 *
 * A paged table has two sizes and they are not the same set: the fifty rows on
 * screen, and the hundred and twenty-five the filter matches. Export used to
 * settle that on the toolbar - a select for the range beside a button whose
 * label carried the arithmetic ("Export this page (50 of 125)") - which put a
 * question nobody has yet asked in the way of the answer, and spent the widest
 * control on the command band on a choice made once a month.
 *
 * So the toolbar says Export, and this says what that will mean. A dialog can
 * afford a full sentence per option where a button label could only afford a
 * bracket, and the counts sit beside each other where they are comparable
 * rather than one at a time.
 *
 * It decides nothing and writes nothing: it hands back a scope and the table
 * that opened it does the work. That is what lets a second table use it.
 */
import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

/**
 * Which rows.
 *
 * `selection` only exists while rows are ticked. The other three are always
 * offered, even where two of them come to the same set.
 */
export type ExportScope = 'selection' | 'page' | 'all' | 'table';

export interface TableExportDialogData {
  /** What the rows are called, for the sentence. "leads", "customers". */
  title: string;
  /** Ticked rows. Zero when nothing is ticked. */
  selectedCount: number;
  /** Rows the table is showing. */
  pageCount: number;
  /** Rows the current filter matches, which may be more than the page holds. */
  totalCount: number;
  /**
   * Rows the table holds with no filter at all, or null when we could not
   * find out.
   *
   * Null rather than falling back to totalCount: those two are the same number
   * whenever nothing is filtered, so a fallback would be indistinguishable
   * from "no filter is on" and would quietly hide the option in the one case
   * it is for - or, worse, offer it with a count that is a guess.
   */
  tableCount: number | null;
}

@Component({
  selector: 'app-table-export-dialog',
  templateUrl: './table-export-dialog.component.html',
  styleUrls: ['./table-export-dialog.component.css'],
})
export class TableExportDialogComponent {
  scope: ExportScope;

  constructor(
    private readonly dialogRef: MatDialogRef<TableExportDialogComponent, ExportScope>,
    @Inject(MAT_DIALOG_DATA) readonly data: TableExportDialogData,
  ) {
    // Ticking rows is the plainest statement of intent there is, so it is the
    // answer already filled in. Offered as a choice rather than imposed: the
    // toolbar used to let a selection silently overrule the range, so a person
    // who ticked two rows in a filter of 125 could not export the 125 without
    // first working out that unticking was the way to do it.
    this.scope = this.hasSelection ? 'selection' : 'page';
  }

  get hasSelection(): boolean { return this.data.selectedCount > 0; }

  /**
   * Whether the filter reaches past the page.
   *
   * No longer decides whether an option is SHOWN - all three always are - but
   * still decides how the page's count is worded, because "50" on its own
   * reads as "there are 50".
   */
  get hasMoreThanPage(): boolean {
    return this.data.totalCount > this.data.pageCount;
  }

  /**
   * The whole table's count, or a dash when the read that would have told us
   * failed.
   *
   * A dash rather than the filtered total: the option is offered either way,
   * and a number that is quietly the wrong one is worse than an admission
   * that we do not have it. The export itself does not depend on this - it
   * asks the server for its own total before it starts walking.
   */
  get tableLabel(): string {
    return this.data.tableCount === null ? '\u2014' : `${this.data.tableCount}`;
  }

  /** The page's count, said against the number it is not - which is the whole
   *  point of asking. A bare "50" reads as "there are 50". */
  get pageLabel(): string {
    return this.hasMoreThanPage
      ? `${this.data.pageCount} of ${this.data.totalCount}`
      : `${this.data.pageCount}`;
  }

  get rowsInScope(): number {
    switch (this.scope) {
      case 'selection': return this.data.selectedCount;
      case 'all': return this.data.totalCount;
      case 'table': return this.data.tableCount ?? this.data.totalCount;
      default: return this.data.pageCount;
    }
  }

  /** Both of the ones that walk the server a page at a time. */
  get readsEveryPage(): boolean {
    return this.scope === 'all' || this.scope === 'table';
  }

  cancel(): void { this.dialogRef.close(); }

  confirm(): void { this.dialogRef.close(this.scope); }
}
